import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Radio,
  Upload, Download, Save, BookOpenText, Music, Music4, Library, Sliders, Drum, Music2, Mic2, Sparkles, AudioLines, Disc3, Share2, Compass, MoreHorizontal, Plug, Repeat, Code2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { getUserToken } from "@/lib/userToken";
import { fetchRecordingStats } from "@/lib/recordingStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export { StatusStrip } from "./StatusPills";

export function AmbientBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 opacity-60">
      <div className="absolute -left-40 top-0 h-[460px] w-[460px] rounded-full bg-fuchsia-600/[0.22] blur-3xl animate-[pulse_8s_ease-in-out_infinite]" />
      <div className="absolute right-[-220px] top-40 h-[540px] w-[540px] rounded-full bg-sky-500/[0.18] blur-3xl animate-[pulse_11s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-emerald-500/[0.10] blur-3xl" />
      {/* Faint vertical scan line — adds a real "studio rack" tone without
          competing with the glow accents. */}
      <div
        className="absolute inset-x-0 top-0 h-[2px] opacity-50"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(232,121,249,0.6) 50%, transparent 100%)" }}
      />
    </div>
  );
}

export function BrandLogo() {
  // Compact wordmark — for share pages and embedded contexts. The main app
  // header uses the full wordmark via HeaderTitle.
  return (
    <img
      src="/brand/elpm-logo.png"
      alt="ELPM Studio"
      data-testid="brand-logo-compact"
      className="h-9 w-auto select-none drop-shadow-[0_2px_14px_rgba(232,121,249,0.22)]"
      draggable="false"
    />
  );
}

export function HeaderTitle() {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-fuchsia-200/65">
        <span className="relative grid h-2 w-2 place-items-center">
          <span className="absolute h-2 w-2 animate-ping rounded-full bg-fuchsia-400/80" />
          <span className="relative h-1.5 w-1.5 rounded-full bg-fuchsia-300" />
        </span>
        <Radio className="h-3.5 w-3.5" /> Web · 64 · Channel · DAW
      </div>
      <h1 className="leading-none tracking-tight">
        <img
          src="/brand/elpm-logo-advanced.png"
          alt="ELPM Advanced Studio"
          data-testid="brand-wordmark"
          className="h-12 w-auto select-none rounded-lg md:h-16 drop-shadow-[0_6px_28px_rgba(232,121,249,0.28)]"
          draggable="false"
        />
        <span className="sr-only">ELPM Advanced Studio</span>
      </h1>
      <p className="mt-3 max-w-3xl font-body text-[12px] leading-5 text-white/55 md:text-[15px] md:leading-6">
        Multitrack recording <span className="text-white/30">·</span> AI voice cloning with consent <span className="text-white/30">·</span> stem extraction <span className="text-white/30">·</span> VST-style effects <span className="text-white/30">·</span> mixer automation <span className="text-white/30">·</span> export-ready mastering.
      </p>
    </div>
  );
}

/* Single shared motion preset — micro-lift on hover so every header button
 * behaves consistently without re-allocating objects per button. */
const BTN_HOVER = { y: -1, transition: { type: "spring", stiffness: 500, damping: 28 } };
const BTN_TAP = { y: 0, scale: 0.97 };

/** Thin wrapper — applies the framer-motion hover/tap micro-animation to any
 *  child element. Children keep their own data-testid/Tailwind styling intact. */
function MotionWrap({ children }) {
  return (
    <motion.div whileHover={BTN_HOVER} whileTap={BTN_TAP} className="inline-flex">
      {children}
    </motion.div>
  );
}

/** A labelled cluster of header tools — Create · Record · Edit · Finish. */
function ToolGroup({ id, label, hint, children }) {
  return (
    <div data-testid={`header-group-${id}`} className="flex flex-wrap items-center gap-1.5">
      <span title={hint} className="mr-1 min-w-[64px] select-none font-display text-[11.5px] font-bold uppercase tracking-[0.22em] text-fuchsia-200/90">
        {label}
      </span>
      {children}
    </div>
  );
}

const MOBILE_GROUPS = [
  { label: "Create", items: [
    { key: "suno", label: "Make music", icon: Music4, cls: "bg-gradient-to-r from-fuchsia-500 via-amber-400 to-orange-500 text-black font-semibold hover:opacity-90" },
  ] },
  { label: "Record", items: [
    { key: "recordings", label: "Recordings", icon: Disc3, optional: true, cls: "border border-rose-400/30 bg-rose-500/[0.10] text-rose-100 hover:bg-rose-500/[0.18]" },
    { key: "backing", label: "Backing Vocals", icon: Mic2, optional: true, cls: "border border-rose-400/30 bg-rose-500/[0.10] text-rose-100 hover:bg-rose-500/[0.18]" },
    { key: "loopstation", label: "Loop Station", icon: Repeat, optional: true, cls: "border border-cyan-400/40 bg-cyan-500/[0.10] text-cyan-100 hover:bg-cyan-500/[0.18]" },
  ] },
  { label: "Edit", items: [
    { key: "mt-editor", label: "Audio Editor", icon: AudioLines, optional: true, cls: "border border-cyan-400/40 bg-cyan-500/[0.08] text-cyan-100 hover:bg-cyan-500/[0.18]" },
    { key: "drum", label: "Drum Machine", icon: Drum, cls: "border border-amber-400/30 bg-amber-500/[0.10] text-amber-100 hover:bg-amber-500/[0.18]" },
    { key: "bass", label: "Bass Player", icon: Music2, cls: "border border-indigo-400/30 bg-indigo-500/[0.10] text-indigo-100 hover:bg-indigo-500/[0.18]" },
    { key: "loops", label: "Loops", icon: AudioLines, cls: "border border-cyan-400/30 bg-cyan-500/[0.10] text-cyan-100 hover:bg-cyan-500/[0.18]" },
    { key: "midi", label: "MIDI", icon: Sliders, cls: "border border-white/[0.08] bg-white/[0.04] text-white/95 hover:bg-white/[0.1]" },
    { key: "jsfx", label: "JSFX", icon: Code2, optional: true, cls: "border border-emerald-400/30 bg-emerald-500/[0.10] text-emerald-100 hover:bg-emerald-500/[0.18]" },
    { key: "plugins", label: "Plugins", icon: Plug, to: "/plugins", cls: "border border-cyan-400/30 bg-cyan-500/[0.10] text-cyan-100 hover:bg-cyan-500/[0.18]" },
  ] },
  { label: "Finish", items: [
    { key: "save", label: "Save Session", icon: Save, cls: "border border-white/[0.08] bg-white/[0.04] text-white/95 hover:bg-white/[0.1]" },
    { key: "export", label: "Export WAV", icon: Download, cls: "border border-white/[0.08] bg-white/[0.04] text-white/95 hover:bg-white/[0.1]" },
    { key: "share", label: "Share Session", icon: Share2, optional: true, cls: "bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-white font-semibold hover:opacity-90" },
    { key: "library", label: "My Songs", icon: Library, cls: "border border-fuchsia-400/30 bg-fuchsia-500/[0.12] text-fuchsia-100 hover:bg-fuchsia-500/20" },
    { key: "explore", label: "Explore", icon: Compass, to: "/explore", cls: "border border-sky-400/30 bg-sky-500/[0.10] text-sky-100 hover:bg-sky-500/[0.18]" },
  ] },
];

export function HeaderActions({ onUpload, onSave, onExport, onShare, onLyrics, onSong, onSuno, onLoopStation, onLibrary, onMidi, onDrum, onBass, onJsfx, onBackingVocals, onLoops, onJam, onMtEditor, onRecordings, busyExport, busyJam, headerUploadRef, onFilePicked }) {
  const baseGhost = "studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-white/[0.08] bg-white/[0.04] text-white/95 hover:bg-white/[0.1]";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // iter-102 — Live activity counts. Polls every 60 s so the badges reflect
  // new songs/recordings without users having to refresh.
  const [songCount, setSongCount] = useState(0);
  const [recordingCount, setRecordingCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      const token = getUserToken();
      try {
        const [songs, recs] = await Promise.allSettled([
          axios.get(`${API}/library/songs`, { params: { user_token: token, limit: 200 } }),
          fetchRecordingStats(),
        ]);
        if (!alive) return;
        if (songs.status === "fulfilled") setSongCount(Array.isArray(songs.value.data) ? songs.value.data.length : 0);
        if (recs.status === "fulfilled") setRecordingCount(recs.value.takes);
      } catch (e) { void e; }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60_000);
    window.addEventListener("elpm:recordings-updated", fetchCounts);
    return () => { alive = false; clearInterval(id); window.removeEventListener("elpm:recordings-updated", fetchCounts); };
  }, []);

  // Helpers — keep raw badge values out of the JSX so the data-attr is
  // only attached when there's an actual count to show (CSS pseudo only
  // renders on `[data-badge]:not([data-badge=""])`).
  const badge = (n) => (n && n > 0 ? (n > 99 ? "99+" : String(n)) : undefined);

  // Wraps an onClick handler so a tap inside the mobile sheet closes it.
  const closeAnd = (fn) => () => { setMobileMenuOpen(false); fn?.(); };

  return (
    <div className="flex flex-col gap-2">
      <input ref={headerUploadRef} type="file" accept="audio/*" className="hidden" onChange={onFilePicked} />

      {/* ──────────  Phone: four primary actions + a "More" sheet  ────────── */}
      <div className="flex flex-wrap gap-1.5 md:hidden">
        <MotionWrap>
          <Button data-testid="header-upload-btn-mobile" onClick={onUpload} className="studio-pill h-9 rounded-2xl px-3 text-[13px] bg-white text-black hover:bg-white/90">
            <Upload className="mr-1.5 h-3.5 w-3.5" />Upload
          </Button>
        </MotionWrap>
        <MotionWrap>
          <Button data-testid="header-jam-btn-mobile" onClick={onJam} disabled={busyJam} className="studio-pill h-9 rounded-2xl px-3 text-[13px] bg-gradient-to-r from-emerald-400 via-cyan-400 to-fuchsia-400 text-black font-semibold hover:opacity-90 disabled:opacity-60">
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />{busyJam ? "Jamming…" : "Jam"}
          </Button>
        </MotionWrap>
        <MotionWrap>
          <Button data-testid="header-song-btn-mobile" onClick={onSong} className="studio-pill h-9 rounded-2xl px-3 text-[13px] bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-400 text-white hover:opacity-90">
            <Music className="mr-1.5 h-3.5 w-3.5" />Compose
          </Button>
        </MotionWrap>
        <MotionWrap>
          <Button data-testid="header-lyrics-btn-mobile" onClick={onLyrics} className="studio-pill h-9 rounded-2xl px-3 text-[13px] bg-gradient-to-r from-fuchsia-500 to-sky-500 text-white hover:opacity-90">
            <BookOpenText className="mr-1.5 h-3.5 w-3.5" />Lyrics
          </Button>
        </MotionWrap>
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              data-testid="header-more-btn"
              variant="secondary"
              className="studio-pill h-9 rounded-2xl px-3 text-[13px] border border-white/15 bg-white/[0.08] text-white hover:bg-white/[0.16]"
            >
              <MoreHorizontal className="mr-1.5 h-3.5 w-3.5" />More
            </Button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            data-testid="header-more-sheet"
            className="glass-card max-h-[82vh] overflow-y-auto rounded-t-3xl border-0 bg-zinc-950/95 p-6 text-white"
          >
            <SheetTitle className="sr-only">More studio actions</SheetTitle>
            {MOBILE_GROUPS.map((g) => (
              <div key={g.label} className="mb-5 last:mb-0">
                <p className="mb-2 font-display text-[12px] font-bold uppercase tracking-[0.22em] text-fuchsia-200/90">{g.label}</p>
                <div className="grid grid-cols-2 gap-2">
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    const handler = { suno: onSuno, loopstation: onLoopStation, "mt-editor": onMtEditor, drum: onDrum, bass: onBass, loops: onLoops, library: onLibrary, recordings: onRecordings, midi: onMidi, save: onSave, export: onExport, share: onShare, backing: onBackingVocals, jsfx: onJsfx }[it.key];
                    if (it.to) {
                      return (
                        <Button key={it.key} asChild data-testid={`header-${it.key}-btn-mobile`} variant="secondary" className={`studio-pill h-12 justify-start rounded-2xl text-[14px] ${it.cls}`}>
                          <Link to={it.to} onClick={() => setMobileMenuOpen(false)}><Icon className="mr-1.5 h-4 w-4" />{it.label}</Link>
                        </Button>
                      );
                    }
                    if (it.optional && !handler) return null;
                    return (
                      <Button key={it.key} data-testid={`header-${it.key}-btn-mobile`} onClick={closeAnd(handler)} variant="secondary" disabled={it.key === "export" && busyExport} className={`studio-pill h-12 justify-start rounded-2xl text-[14px] ${it.cls}`}>
                        <Icon className="mr-1.5 h-4 w-4" />{it.key === "export" && busyExport ? "Bouncing…" : it.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </SheetContent>
        </Sheet>
      </div>

      {/* ──────────  Desktop: every tool, grouped by what you want to do  ────────── */}
      <div data-testid="header-tool-groups" className="hidden md:flex md:flex-col md:gap-2">
        <ToolGroup id="create" label="Create" hint="Start a song">
          <MotionWrap>
            <Button data-testid="header-upload-btn" onClick={onUpload} className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] bg-white text-black hover:bg-white/90">
              <Upload className="mr-1.5 h-3.5 w-3.5" />Upload
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button
              data-testid="header-suno-btn"
              onClick={onSuno}
              className="studio-pill studio-pill-headliner h-8 rounded-2xl px-3 text-[12.5px] bg-gradient-to-r from-fuchsia-500 via-amber-400 to-orange-500 text-black font-semibold"
            >
              <Music4 className="mr-1.5 h-3.5 w-3.5" />Make music
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button data-testid="header-song-btn" onClick={onSong} className="studio-pill pill-anim-compose h-8 rounded-2xl px-3 text-[12.5px] bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-400 text-white hover:opacity-90 shadow-[0_0_22px_rgba(244,63,94,0.32)]">
              <Music className="mr-1.5 h-3.5 w-3.5" />Compose
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button data-testid="header-lyrics-btn" onClick={onLyrics} className="studio-pill pill-anim-lyrics h-8 rounded-2xl px-3 text-[12.5px] bg-gradient-to-r from-fuchsia-500 to-sky-500 text-white hover:opacity-90">
              <BookOpenText className="mr-1.5 h-3.5 w-3.5" />Lyrics
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button
              data-testid="header-jam-btn"
              onClick={onJam}
              disabled={busyJam}
              className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] bg-gradient-to-r from-emerald-400 via-cyan-400 to-fuchsia-400 text-black font-semibold hover:opacity-90 disabled:opacity-60 shadow-[0_0_24px_rgba(56,189,248,0.28)]"
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />{busyJam ? "Jamming…" : "Jam"}
            </Button>
          </MotionWrap>
        </ToolGroup>

        <ToolGroup id="record" label="Record" hint="Your voice and takes">
          {onRecordings && (
            <MotionWrap>
              <Button data-testid="header-recordings-btn" onClick={onRecordings} variant="secondary" data-badge={badge(recordingCount)} className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-rose-400/30 bg-rose-500/[0.10] text-rose-100 hover:bg-rose-500/[0.18]">
                <Disc3 className="mr-1.5 h-3.5 w-3.5" />Recordings
              </Button>
            </MotionWrap>
          )}
          {onBackingVocals && (
            <MotionWrap>
              <Button data-testid="header-backing-vocals-btn" onClick={onBackingVocals} variant="secondary" className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-rose-400/30 bg-gradient-to-r from-fuchsia-500/[0.10] to-rose-500/[0.10] text-rose-100 hover:from-fuchsia-500/20 hover:to-rose-500/20">
                <Mic2 className="mr-1.5 h-3.5 w-3.5" />Backing Vocals
              </Button>
            </MotionWrap>
          )}
          {onLoopStation && (
            <MotionWrap>
              <Button
                data-testid="header-loopstation-btn"
                onClick={onLoopStation}
                className="studio-pill pill-anim-loop h-8 rounded-2xl px-3 text-[12.5px] border border-cyan-400/40 bg-cyan-500/[0.10] text-cyan-100 hover:bg-cyan-500/[0.18]"
              >
                <Repeat className="mr-1.5 h-3.5 w-3.5" />Loop Station
              </Button>
            </MotionWrap>
          )}
        </ToolGroup>

        <ToolGroup id="edit" label="Edit" hint="Shape the sound">
          {onMtEditor && (
            <MotionWrap>
              <Button
                data-testid="header-mt-editor-btn"
                onClick={onMtEditor}
                className="studio-pill pill-anim-waveform h-8 rounded-2xl px-3 text-[12.5px] border border-cyan-400/40 bg-cyan-500/[0.08] text-cyan-100 hover:bg-cyan-500/[0.18]"
              >
                <AudioLines className="mr-1.5 h-3.5 w-3.5" />Audio Editor
              </Button>
            </MotionWrap>
          )}
          <MotionWrap>
            <Button data-testid="header-drum-btn" onClick={onDrum} variant="secondary" className="studio-pill pill-anim-drum h-8 rounded-2xl px-3 text-[12.5px] border border-amber-400/30 bg-amber-500/[0.10] text-amber-100 hover:bg-amber-500/[0.18]">
              <Drum className="mr-1.5 h-3.5 w-3.5" />Drum Machine
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button data-testid="header-bass-btn" onClick={onBass} variant="secondary" className="studio-pill pill-anim-bass h-8 rounded-2xl px-3 text-[12.5px] border border-indigo-400/30 bg-indigo-500/[0.10] text-indigo-100 hover:bg-indigo-500/[0.18]">
              <Music2 className="mr-1.5 h-3.5 w-3.5" />Bass Player
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button data-testid="header-loops-btn" onClick={onLoops} variant="secondary" className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-cyan-400/30 bg-cyan-500/[0.10] text-cyan-100 hover:bg-cyan-500/[0.18]">
              <AudioLines className="mr-1.5 h-3.5 w-3.5" />Loops
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button data-testid="header-midi-btn" onClick={onMidi} variant="secondary" className={baseGhost}>
              <Sliders className="mr-1.5 h-3.5 w-3.5" />MIDI
            </Button>
          </MotionWrap>
          {onJsfx && (
            <MotionWrap>
              <Button data-testid="header-jsfx-btn" onClick={onJsfx} variant="secondary" className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-emerald-400/30 bg-emerald-500/[0.10] text-emerald-100 hover:bg-emerald-500/[0.18]">
                <Code2 className="mr-1.5 h-3.5 w-3.5" />JSFX
              </Button>
            </MotionWrap>
          )}
          <MotionWrap>
            <Button
              asChild
              data-testid="header-plugins-btn"
              variant="secondary"
              className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-cyan-400/30 bg-cyan-500/[0.10] text-cyan-100 hover:bg-cyan-500/[0.18]"
            >
              <Link to="/plugins">
                <Plug className="mr-1.5 h-3.5 w-3.5" />Plugins
              </Link>
            </Button>
          </MotionWrap>
        </ToolGroup>

        <ToolGroup id="finish" label="Finish" hint="Save, export and share">
          <MotionWrap>
            <Button data-testid="header-save-btn" onClick={onSave} variant="secondary" className={baseGhost}>
              <Save className="mr-1.5 h-3.5 w-3.5" />Save Session
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button data-testid="header-export-btn" onClick={onExport} variant="secondary" className={baseGhost} disabled={busyExport}>
              <Download className="mr-1.5 h-3.5 w-3.5" />{busyExport ? "Bouncing…" : "Export WAV"}
            </Button>
          </MotionWrap>
          {onShare && (
            <MotionWrap>
              <Button
                data-testid="header-share-btn"
                onClick={onShare}
                className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-white font-semibold shadow-[0_0_22px_rgba(52,211,153,0.32)] hover:opacity-90"
              >
                <Share2 className="mr-1.5 h-3.5 w-3.5" />Share Session
              </Button>
            </MotionWrap>
          )}
          <MotionWrap>
            <Button data-testid="header-library-btn" onClick={onLibrary} variant="secondary" data-badge={badge(songCount)} className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-fuchsia-400/30 bg-fuchsia-500/[0.12] text-fuchsia-100 hover:bg-fuchsia-500/20">
              <Library className="mr-1.5 h-3.5 w-3.5" />My Songs
            </Button>
          </MotionWrap>
          <MotionWrap>
            <Button
              asChild
              data-testid="header-explore-btn"
              variant="secondary"
              className="studio-pill h-8 rounded-2xl px-3 text-[12.5px] border border-sky-400/30 bg-sky-500/[0.10] text-sky-100 hover:bg-sky-500/[0.18]"
            >
              <Link to="/explore" target="_blank" rel="noopener noreferrer">
                <Compass className="mr-1.5 h-3.5 w-3.5" />Explore
              </Link>
            </Button>
          </MotionWrap>
        </ToolGroup>
      </div>
    </div>
  );
}
